import { Injectable } from '@angular/core';
import { SortDirection } from '@angular/material/sort';

import { createSortingDataAccessor, GROUP_BY_KEY } from '@blaze-shared/datasets-domain';
import { compareBy } from '@blaze-shared/platform-util';
import { GroupByRow, isGroupByRow } from '@blaze-shared/schema-domain';
import { SortableItem } from '@blaze-shared/shared-util';

interface GroupWithItems<T> {
  item: GroupByRow;
  subgroups: GroupWithItems<T>[];
  subitems: T[];
}

type SortingDataAccessorFn<T> = (data: T, sortHeaderId: string) => string | number;

@Injectable({
  providedIn: 'root',
})
export class TableSortService {
  /**
   * Sorts items in a table that are organized into groups, based on a specified key.
   * Sorting is performed within each group, not across the entire table.
   */
  sortGroupedTableRows<T extends SortableItem>(
    items: T[],
    sort: {
      active: string;
      direction: SortDirection;
    },
  ): T[] {
    const groupTree = this.buildGroupTree<T>(items);
    return this.flattenGroups(sort, groupTree, createSortingDataAccessor());
  }

  private buildGroupTree<T extends SortableItem>(items: T[]): GroupWithItems<T>[] {
    const groupsLvl0: GroupWithItems<T>[] = [];
    const groupsStack: GroupWithItems<T>[] = [];
    let currentGroup: GroupWithItems<T> | undefined;

    for (const item of items) {
      if (!isGroupByRow(item)) {
        if (!currentGroup) {
          throw new Error('no group for item', item);
        }
        currentGroup.subitems.push(item);
        continue;
      }

      const group: GroupWithItems<T> = { item, subgroups: [], subitems: [] };

      if (!currentGroup || item.__level === 0) {
        groupsLvl0.push(group);
        groupsStack.length = 0;
      } else if (item.__level > currentGroup.item.__level) {
        groupsStack.push(currentGroup);
        groupsStack.at(-1)?.subgroups.push(group);
      } else if (item.__level === currentGroup.item.__level) {
        groupsStack.at(-1)?.subgroups.push(group);
      } else {
        groupsStack.pop();
        groupsStack.at(-1)?.subgroups.push(group);
      }

      currentGroup = group;
    }

    return groupsLvl0;
  }

  private flattenGroups<T>(
    sort: { active: string; direction: SortDirection },
    groups: GroupWithItems<T>[],
    sortingDataAccessor: SortingDataAccessorFn<T>,
  ): T[] {
    const isSortingByGroupKey = sort.active === GROUP_BY_KEY;
    const isAscending = sort.direction === 'asc';

    const sortedGroups = isSortingByGroupKey
      ? groups.toSorted(compareBy((g) => g.item.__groupBy, isAscending))
      : groups;

    return sortedGroups.flatMap((group) => {
      const items = this.getSortedGroupItems(group, sort, isSortingByGroupKey, sortingDataAccessor);
      return [group.item as T, ...items];
    });
  }

  private getSortedGroupItems<T>(
    group: GroupWithItems<T>,
    sort: { active: string; direction: SortDirection },
    isSortingByGroupKey: boolean,
    sortingDataAccessor: SortingDataAccessorFn<T>,
  ): T[] {
    if (!group.subitems.length) {
      return this.flattenGroups(sort, group.subgroups, sortingDataAccessor);
    }
    if (isSortingByGroupKey) {
      return group.subitems;
    }
    const isAscending = sort.direction === 'asc';
    return group.subitems.toSorted(
      compareBy((item) => sortingDataAccessor(item, sort.active), isAscending),
    );
  }
}
